/*
  Collection list slider that wraps around endlessly.

  Dawn's slider-component is native scroll-snap, which can't loop, so Flickity
  takes over wherever the CSS switches it on via watchCSS (see
  section-collection-list.css). Where it's off, the inherited native behaviour
  applies. The theme's own prev/next buttons and counter drive both.
*/
if (!customElements.get('collection-list-slider')) {
  customElements.define(
    'collection-list-slider',
    class CollectionListSlider extends SliderComponent {
      constructor() {
        super();
        if (!this.slider || !this.nextButton || typeof Flickity === 'undefined') return;

        // Resolves the CSS fallback offset (a calc of %s) to pixels, for when
        // there's no heading to line up with. A span, as base.css hides empty divs.
        this.offsetProbe = document.createElement('span');
        this.offsetProbe.className = 'collection-list__offset-probe';
        this.offsetProbe.setAttribute('aria-hidden', 'true');
        this.slider.appendChild(this.offsetProbe);

        this.flickity = new Flickity(this.slider, {
          watchCSS: true,
          wrapAround: true,
          cellAlign: 'left',
          cellSelector: '.collection-list__item',
          prevNextButtons: false,
          pageDots: false,
          imagesLoaded: true,
        });

        // Flickity has no start-padding option, so offset its alignment point to
        // line the selected slide up with the heading. When the list bleeds, the
        // wrapped slides then fill the gutters on both sides.
        const getOffset = () => this.getStartOffset();
        const getSize = this.flickity.getSize;
        this.flickity.getSize = function () {
          getSize.call(this);
          this.cursorPosition += getOffset();
        };
        this.flickity.resize();

        // Flickity focuses the list on grab so arrow keys work afterwards; flag
        // that so the CSS only shows a focus ring for keyboard focus.
        this.flickity.on('pointerDown', () => this.slider.classList.add('is-pointer-focus'));
        this.slider.addEventListener('keydown', () => this.slider.classList.remove('is-pointer-focus'));
        this.slider.addEventListener('blur', () => this.slider.classList.remove('is-pointer-focus'));

        this.flickity.on('select', () => this.update());
        this.flickity.on('activate', () => this.update());
        this.flickity.on('deactivate', () => this.resetPages());
        this.update();
      }

      getStartOffset() {
        const title = this.closest('.collection-list-wrapper')?.querySelector('.title-wrapper-with-link');
        if (!title) return this.offsetProbe.offsetWidth;

        // The wrapper, not the heading, as the heading can be mid slide-in.
        const titleStart = title.getBoundingClientRect().left + parseFloat(getComputedStyle(title).paddingLeft);
        return Math.max(0, titleStart - this.slider.getBoundingClientRect().left);
      }

      get isLooping() {
        return Boolean(this.flickity && this.flickity.isActive);
      }

      initPages() {
        if (this.isLooping) return this.update();
        super.initPages();
      }

      update() {
        if (!this.isLooping) return super.update();

        if (this.currentPageElement && this.pageTotalElement) {
          this.currentPageElement.textContent = this.flickity.selectedIndex + 1;
          this.pageTotalElement.textContent = this.flickity.slides.length;
        }
        this.updateDots(this.flickity.selectedIndex + 1, this.flickity.slides.length);
        this.prevButton.removeAttribute('disabled');
        this.nextButton.removeAttribute('disabled');
      }

      goToPage(page) {
        if (!this.isLooping) return super.goToPage(page);
        this.flickity.select(page - 1);
      }

      onButtonClick(event) {
        if (!this.isLooping) return super.onButtonClick(event);

        event.preventDefault();
        event.currentTarget.name === 'next' ? this.flickity.next() : this.flickity.previous();
      }
    }
  );
}
